import React, {
	memo,
	useCallback,
	useRef,
	useEffect,
	useState,
	LegacyRef,
} from "react";
import { Row, Col, Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { ReloadOutlined } from "@ant-design/icons";
// import { useParams, useNavigate } from "react-router-dom"; // TODO: Uncomment when group ID URL functionality is needed

import { MainLayout } from "layouts";
import {
	PostForm,
	ScrollToTopButton,
	FilterTag,
	HelmetPageTitle,
	Loading,
	CommunityWelcomeModal,
} from "components";
import { TStore, IPostsState } from "models";
import styles from "./styles.module.scss";
import {
	setFilterTagsMiddleware,
	getPosts,
	setQueryPostsAction,
	notify,
	SetWelcomeModal,
	// appSetGroup,
	// getGroupBySlug, // TODO: Uncomment when group ID URL functionality is needed
} from "store";
import { PostsData } from "./components";
import { getUnreadNotificationsCounterSagaAction } from "store/actions/notifications";
import DirectoryMemberOptedOutPanel from "components/DirectoryMemberOptedOutPanel";
// import { isNumericString } from "utils"; // TODO: Uncomment when group ID URL functionality is needed

const stateSelectorHandle = createSelector(
	(state: TStore) => state.posts,
	(state: TStore) => state.community.mainTags,
	(state: TStore) => state.app.currentCommunityId,
	(state: TStore) => state.app.currentGroupId,
	(state: TStore) => state.editProfile.data,
	(state: TStore) => state.app.welcomeModal,
	(state: TStore) => state.groups,
	(state: TStore) => state.community.data, // <-- add community data
	(
		posts: IPostsState,
		communityTags,
		currentCommunityId,
		currentGroupId,
		editProfileData,
		welcomeModal,
		groups,
		communityData,
	) => ({
		posts,
		communityTags,
		currentCommunityId,
		currentGroupId,
		editProfileData,
		welcomeModal,
		groups,
		communityData,
	}),
);

const Dashboard = () => {
	const ref: LegacyRef<HTMLDivElement> | null = useRef(null);
	const dispatch = useDispatch();
	const listRef = useRef<any>();
	const [position, setPosition] = useState<any>();
	const [initializingPostsDataByTags, setInitializingPostsDataByTags] =
		useState(false);
	const [hideTheComponent, setHideTheComponent] = useState(false);
	// const navigate = useNavigate();

	const handleHideTheComponent = () => {
		setHideTheComponent(true);
	};
	const stateSelector = useCallback(stateSelectorHandle, []);
	const {
		posts,
		communityTags,
		currentCommunityId,
		currentGroupId,
		editProfileData,
		welcomeModal,
		// groups,
		communityData,
	} = useSelector(stateSelector);
	const { selectedTags, meta, query, shouldRefresh } = posts;

	const handleSearchPosts = useCallback(
		(onSuccess?: () => void) => {
			if (!currentCommunityId) {
				console.error("Missing arguments!");
				return;
			}

			dispatch(
				getPosts({
					resetPosts: true,
					onError: () => {
						notify(
							"error",
							"An error occurred. Please try again later.",
						);
					},
					onSuccess,
				}),
			);
			const mainContainer = document.getElementsByTagName("main")[0];
			if (mainContainer) {
				mainContainer.scrollTop = 0;
			}
		},
		[dispatch, currentCommunityId],
	);

	const handleFilterTagsPosts = useCallback(
		(tags?: string[]) => {
			setInitializingPostsDataByTags(true);
			let newTag: string[] = [];
			if (tags && tags.length) {
				newTag = tags.filter(
					(item: any) => selectedTags && !selectedTags.includes(item),
				);
			}
			dispatch(setFilterTagsMiddleware({ tag: newTag }));
			handleSearchPosts(() => setInitializingPostsDataByTags(false));

			if (newTag.length === 0) {
				dispatch(setQueryPostsAction({ query: "" }));
			}
		},
		[dispatch, selectedTags, handleSearchPosts],
	);

	useEffect(() => {
		if (shouldRefresh)
			setPosition(
				listRef.current.firstElementChild.nextElementSibling.getBoundingClientRect(),
			);
	}, [shouldRefresh]);

	useEffect(() => {
		handleSearchPosts();
	}, [currentGroupId, currentCommunityId, handleSearchPosts]);

	useEffect(() => {
		dispatch(getUnreadNotificationsCounterSagaAction());
		return () => {
			dispatch(setQueryPostsAction({ query: "" }));
		};
	}, [dispatch]);

	// TODO: Group ID in URL functionality - commented out for now
	// useEffect(() => {
	// 	if (groupId) {
	// 		// Check if groupId is a number (ID) or a string (slug)
	// 		if (isNumericString(groupId)) {
	// 			// It's a numeric ID
	// 			const numericGroupId = parseInt(groupId, 10);
	// 			
	// 			// If groups are still loading, wait for them to load
	// 			if (groups.loading) {
	// 				return;
	// 			}
	// 			
	// 			// If groups data is not loaded yet and not loading, groups might not have been fetched
	// 			if (groups.data.length === 0 && !groups.loading) {
	// 				// Set the group ID temporarily and wait for groups to load
	// 				dispatch(appSetGroup({ currentGroupId: numericGroupId }));
	// 				return;
	// 			}
	// 			
	// 			// Check if the group exists in the groups data
	// 			const groupExists = groups.data.find(group => group.id === numericGroupId);
	// 			
	// 			if (groupExists) {
	// 				// Group exists, set it as current
	// 				dispatch(appSetGroup({ currentGroupId: numericGroupId }));
	// 			} else {
	// 				// Group doesn't exist or user doesn't have access, redirect to dashboard without group
	// 				console.warn(`Group with ID ${groupId} not found or not accessible`);
	// 				dispatch(appSetGroup({ currentGroupId: null }));
	// 				navigate("/dashboard", { replace: true });
	// 			}
	// 		} else {
	// 			// It's a slug, we need to fetch the group by slug
	// 			if (currentCommunityId) {
	// 				dispatch(getGroupBySlug({
	// 					communityId: currentCommunityId,
	// 					slug: groupId,
	// 					successCallback: (group) => {
	// 						// Group found by slug, set it as current but keep the slug URL
	// 						dispatch(appSetGroup({ currentGroupId: group.id }));
	// 					},
	// 					errorCallback: () => {
	// 						// Group not found by slug, redirect to dashboard without group
	// 						console.warn(`Group with slug "${groupId}" not found or not accessible`);
	// 						dispatch(appSetGroup({ currentGroupId: null }));
	// 						navigate("/dashboard", { replace: true });
	// 					},
	// 				}));
	// 			}
	// 		}
	// 	} else {
	// 		// If no groupId in URL, clear the current group selection
	// 		dispatch(appSetGroup({ currentGroupId: null }));
	// 	}
	// }, [dispatch, groupId, groups.data, groups.loading, navigate, currentCommunityId]);

	// TODO: Group validation when data becomes available - commented out for now
	// // Validate current group when groups data becomes available
	// useEffect(() => {
	// 	// Only run this validation if:
	// 	// 1. We have a current group ID set
	// 	// 2. Groups data is available (not loading and has data)
	// 	// 3. We have a groupId in the URL
	// 	if (currentGroupId && !groups.loading && groups.data.length > 0 && groupId) {
	// 		// Check if the URL parameter is numeric (ID) or slug
	// 		if (isNumericString(groupId)) {
	// 			// URL parameter is numeric, validate against group ID
	// 			const numericGroupId = parseInt(groupId, 10);
	// 			
	// 			// Check if the current group ID matches the URL and exists in groups data
	// 			if (currentGroupId === numericGroupId) {
	// 				const groupExists = groups.data.find(group => group.id === numericGroupId);
	// 				
	// 				if (!groupExists) {
	// 					// Group doesn't exist or user doesn't have access, redirect to dashboard without group
	// 					console.warn(`Group with ID ${groupId} not found or not accessible`);
	// 					dispatch(appSetGroup({ currentGroupId: null }));
	// 					navigate("/dashboard", { replace: true });
	// 				}
	// 			}
	// 		} else {
	// 			// URL parameter is a slug, validate that the current group ID corresponds to a group with this slug
	// 			const groupWithSlug = groups.data.find(group => 
	// 				group.id === currentGroupId && group.slug === groupId
	// 			);
	// 			
	// 			if (!groupWithSlug) {
	// 				// Either the group doesn't exist, user doesn't have access, or slug doesn't match
	// 				// We need to re-fetch by slug to make sure we have the right group
	// 				if (currentCommunityId) {
	// 					dispatch(getGroupBySlug({
	// 						communityId: currentCommunityId,
	// 						slug: groupId,
	// 						successCallback: (group) => {
	// 							// Update the current group if it's different
	// 							if (group.id !== currentGroupId) {
	// 								dispatch(appSetGroup({ currentGroupId: group.id }));
	// 							}
	// 						},
	// 						errorCallback: () => {
	// 							console.warn(`Group with slug "${groupId}" not found or not accessible`);
	// 							dispatch(appSetGroup({ currentGroupId: null }));
	// 							navigate("/dashboard", { replace: true });
	// 						},
	// 					}));
	// 				}
	// 			}
	// 		}
	// 	}
	// }, [currentGroupId, groups.data, groups.loading, groupId, dispatch, navigate, currentCommunityId]);

	const isDirectoryEnabled = communityData?.services?.find(
		(s: any) => s.label === "Directory"
	)?.show !== false;

	return (
		<>
			<HelmetPageTitle screenTitle={"Feed"} />
			<MainLayout>
				<div className={styles.container}>
					<Row style={{ flexShrink: 0 }}>
						<Col span={24} order={1}>
							<PostForm uniqueKey={`create-post-form`} />
						</Col>
					</Row>

					<Row style={{ flexShrink: 0 }}>
						<Col span={24} order={1}>
							<div
								className={styles.tagFiltersContainer}
								ref={ref}
							>
								<FilterTag
									onClick={handleFilterTagsPosts}
									tagsList={communityTags.tags}
									selectedTags={selectedTags}
									query={query}
								/>
							</div>
						</Col>
					</Row>
					{posts.data.length === 0 && !posts.loading && (
						<Row style={{ flexShrink: 0 }}>
							<Col span={24} order={1}>
								{
									<div className={"emptyPlaceholder"}>
										{"No posts, yet."}
									</div>
								}
							</Col>
						</Row>
					)}
					{parseInt(editProfileData?.user?.id) &&
					editProfileData?.user?.member_directory_opt_in === false &&
					isDirectoryEnabled ? (
						<Row style={{ flexShrink: 0 }}>
							<Col span={24} order={1}>
								{!hideTheComponent && (
									<DirectoryMemberOptedOutPanel
										memberDirectoryOptIn={
											editProfileData.user
												.member_directory_opt_in
										}
										showHidebutton={
											hideTheComponent ||
											!editProfileData.user
												.member_directory_opt_in
										}
										handleHideButton={
											handleHideTheComponent
										}
									/>
								)}
							</Col>
						</Row>
					) : null}
					<Row gutter={[32, 24]}>
						<Col span={24} order={2} ref={listRef}>
							{shouldRefresh && (
								<Button
									type="primary"
									className={styles.newContent}
									style={{
										background:
											meta.appearance?.primary_color,
										borderColor:
											meta.appearance?.primary_color,
										position: position
											? "sticky"
											: undefined,
									}}
									onClick={() => handleSearchPosts()}
								>
									<ReloadOutlined
										style={{
											position: "absolute",
											left: 15,
											top: 12,
										}}
									/>
									{"New content available"}
									<span>{"click to refresh"}</span>
								</Button>
							)}
							{!initializingPostsDataByTags && <PostsData />}
						</Col>
					</Row>
					{initializingPostsDataByTags && <Loading />}
					<ScrollToTopButton parentRef={ref} />
				</div>
			</MainLayout>
			<CommunityWelcomeModal
				isOpen={welcomeModal}
				onClose={() => dispatch(SetWelcomeModal(false))}
				mainButtonHandler={() => dispatch(SetWelcomeModal(false))}
			/>
		</>
	);
};

export default memo(Dashboard);
